import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, type Path, type Resolver, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import type { ProductInput } from "@nh/shared";
import { Alert, Form, FormError } from "@/shared/ui/core";
import { FormWizard, useStepFields, useWizard, useWizardEnter } from "@/shared/ui/core";
import { PageSkeleton } from "@/shared/ui/ui";
import { PageHeader } from "@/shared/ui/layout";
import { isApiError } from "@/shared/api/client";
import { AdminGuard } from "@/features/admin/components";
import { ProductFormActions } from "./components/ProductFormActions";
import { ProductTabs } from "./components/ProductTabs";
import { ProductWizardBody } from "./components/ProductWizardBody";
import {
  emptyProductFormValues,
  productFormSchema,
  productToFormValues,
  toProductUpdate,
  type ProductFormValues,
} from "./productForm";
import { fieldsOfStep, PRODUCT_STEPS, stepOfField } from "./productSteps";
import { useAdminProduct, useCreateProduct, useUpdateProduct } from "./products.queries";
import { useApiErrorMessage } from "./useApiErrorMessage";

/**
 * Crear y editar un producto: un asistente de SIETE pasos.
 *
 * Los siete son los que Anahí dictó, y su lista manda sobre el alto: dos
 * de ellos se pasan de una vista a 1440×900 y se recorren con la rueda.
 * Ver `productSteps.ts`.
 *
 * ── Una sola pantalla para crear y para editar ──────────────────────
 * Lo que cambia entre los dos casos es de dónde salen los valores
 * iniciales, si el slug se puede tocar y a qué endpoint va el envío.
 * Todo lo demás —los campos, la validación, los dos idiomas, la
 * conversión a centavos— es idéntico, y duplicarlo garantiza que un
 * arreglo se aplique solo en una mitad.
 *
 * ── UN formulario, no diez ──────────────────────────────────────────
 * Hay un solo `useForm` para todo el wizard. Un formulario por paso
 * obligaría a copiar los valores de uno a otro a mano y, el día que
 * alguien retrocediera a corregir una letra, encontraría el paso vacío.
 * Con uno solo, ir y volver no pierde nada porque nunca se desmontó el
 * estado — solo los campos.
 *
 * ── UNA petición ────────────────────────────────────────────────────
 * Los pasos son interfaz. El producto se guarda en una sola llamada:
 * partir el envío dejaría productos a medio crear cada vez que alguien
 * abandona en el paso cuatro.
 *
 * ── Validar solo lo que ya se vio ───────────────────────────────────
 * "Siguiente" valida ÚNICAMENTE los campos de su paso. Validar el
 * formulario entero para avanzar sacaría en rojo el precio y el slug de
 * alguien que todavía va escribiendo la descripción.
 *
 * ── Y volver al paso donde está el error ────────────────────────────
 * Si el servidor señala un campo del paso 3 estando en el 7, se regresa
 * a ese paso con el mensaje puesto y el foco en el campo. Un error
 * pintado en un paso oculto es un botón de guardar que no hace nada y
 * nadie sabe por qué.
 */
export function AdminProductFormPage() {
  return (
    <AdminGuard>
      <ProductFormScreen />
    </AdminGuard>
  );
}

function ProductFormScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const existing = useAdminProduct(id);
  const create = useCreateProduct();
  const update = useUpdateProduct(id ?? "");
  const [submitError, setSubmitError] = useState<string | null>(null);

  /*
   * Ya NO se piden aquí las categorías del listado.
   *
   * Salían de `distinct("category")` sobre los productos existentes y
   * eran solo sugerencias sobre un campo de texto libre. Ahora la
   * categoría y el tipo son catálogos de verdad, y quien los pide es el
   * componente que los pinta (`CatalogSelectField`): esta pantalla no
   * tiene por qué saber qué listas necesita un paso concreto, y pedirlas
   * aquí obligaría a pasar dos props más a través de tres componentes.
   */
  const form = useForm<ProductFormValues, unknown, ProductInput>({
    /*
     * El `as` documenta una diferencia real, no la tapa.
     *
     * `zodResolver` se tipa como "recibe lo que el esquema ACEPTA", y lo
     * que este esquema devuelve tras el `.transform().pipe()` es un
     * `ProductInput` — otra forma distinta de la de los controles. La
     * varianza no encaja; el comportamiento sí, y es exactamente el que
     * hace falta: se le pasan los valores del formulario y responde con
     * los errores en las rutas de los campos.
     */
    resolver: zodResolver(productFormSchema) as unknown as Resolver<
      ProductFormValues,
      unknown,
      ProductInput
    >,
    defaultValues: emptyProductFormValues(),
    mode: "onSubmit",
  });

  const loadErrorMessage = useApiErrorMessage(existing.error);

  /*
   * `reset` y no `setValue` campo a campo: reset REEMPLAZA los valores por
   * omisión, así que a partir de aquí "sucio" significa "distinto de lo
   * que hay guardado" —que es lo que `SlugField` consulta para saber si
   * alguien tocó el slug a mano.
   */
  useEffect(() => {
    if (existing.data) form.reset(productToFormValues(existing.data));
  }, [existing.data, form]);

  /*
   * El reparto de campos por paso, atado a los valores de AHORA.
   *
   * `fieldsOfStep` expande los mapas de precio con las monedas que el
   * formulario tiene en este momento (`prices.MXN`, `prices.EUR`…): sin
   * expandir, "Siguiente" en el paso de precios dependería de cómo
   * react-hook-form interpreta una ruta que apunta a un objeto entero.
   *
   * Se memoriza porque de él cuelgan el foco al cambiar de paso y el
   * salto tras un guardado fallido: una identidad nueva en cada render
   * volvería a disparar los dos efectos sin que nada hubiera cambiado.
   */
  const stepFieldsOf = useCallback(
    (step: number) => fieldsOfStep(step, form.getValues()),
    [form],
  );

  /*
   * Qué se valida al avanzar, dónde va el foco y a qué paso lleva un
   * guardado que falló. Las tres salen del MISMO reparto, y por eso salen
   * de la misma pieza. Ver `useStepFields`.
   */
  const { validateStep, focusStepError, firstStepWithError } = useStepFields(
    form,
    stepFieldsOf,
    PRODUCT_STEPS.length,
  );

  const wizard = useWizard({
    total: PRODUCT_STEPS.length,
    canAdvance: validateStep,
  });

  /**
   * Envía y, si el servidor devolvió el fallo señalando campos, lleva de
   * vuelta al paso del PRIMERO de ellos.
   *
   * Al primero y no al último: quien edita corrige en el orden en que
   * escribió, y dejarlo en el paso 7 con otro error esperando en el 3
   * son dos viajes en lugar de uno.
   */
  const submit: SubmitHandler<ProductInput> = async (input) => {
    setSubmitError(null);
    try {
      if (isEdit && id) {
        await update.mutateAsync(toProductUpdate(input));
      } else {
        await create.mutateAsync(input);
      }
      navigate("/admin/products");
    } catch (error) {
      /*
       * El servidor valida otra vez, y sabe cosas que el navegador no:
       * que ese slug ya existe, por ejemplo. Cuando señala campos, el
       * aviso va EN el campo —las rutas del contrato coinciden con los
       * nombres del formulario— y no en un cartel genérico arriba que
       * obliga a buscar cuál de los cuarenta y cinco es.
       */
      if (isApiError(error) && error.fields) {
        let firstStep = Number.POSITIVE_INFINITY;

        for (const [field, messages] of Object.entries(error.fields)) {
          const message = messages[0];
          if (!message) continue;
          form.setError(field as Path<ProductFormValues>, {
            type: "server",
            message,
          });
          const step = stepOfField(field);
          if (step >= 0) firstStep = Math.min(firstStep, step);
        }

        if (Number.isFinite(firstStep)) wizard.goTo(firstStep);
        return;
      }

      setSubmitError(
        isApiError(error) ? error.message : t("Algo salió mal. Vuelve a intentarlo."),
      );
    }
  };

  // Enter avanza de paso; no envía a medias. Ver `useWizardEnter`.
  const handleKeyDown = useWizardEnter(wizard);

  if (isEdit && existing.isPending) {
    return <PageSkeleton />;
  }

  if (isEdit && existing.isError) {
    return (
      <Alert variant="error" title={t("No se pudo cargar el producto")}>
        {loadErrorMessage}
      </Alert>
    );
  }

  const submitLabel = isEdit ? t("Guardar cambios") : t("Crear producto");

  return (
    <>
      {/*
        Sin rótulo y sin descripción (Anahí, 26-ago). El rótulo
        "CATÁLOGO" repetía lo que ya dicen la barra lateral y la superior,
        y la descripción era una frase que se lee una vez y luego estorba
        siete pasos seguidos.

        No es solo limpieza: entre los dos se llevaban 62 px, y los pasos
        de Información y Media se pasaban de la pantalla por 57 y 55. Al
        quitarlos, caben.
      */}
      <PageHeader
        compact
        title={isEdit ? t("Editar producto") : t("Nuevo producto")}
        /*
          Las pestañas van en la MISMA fila que el título, arriba a la
          derecha (Anahí, 26-ago). Debajo se llevaban un renglón entero
          para dos palabras, en un formulario que pelea por caber; y al
          lado del título se leen como lo que son —dos caras de la misma
          ficha— y no como el primer control del formulario.

          Solo al editar: en el alta no hay ficha que detallar todavía.
        */
        actions={isEdit && id ? <ProductTabs id={id} active="producto" /> : undefined}
        // Sin `divider`: la card de vidrio que viene justo debajo ya pone
        // su propio borde, y dos líneas horizontales a 30 px una de otra
        // separan lo mismo dos veces. Los 25 px que costaba la regla y su
        // relleno son 25 px que se lleva el paso.
      />

      <FormError error={submitError} onDismiss={() => setSubmitError(null)} />

      <Form form={form} onSubmit={submit} onKeyDown={handleKeyDown}>
        {/*
          ── El armazón, que ya no se escribe aquí ─────────────────────
          La card de vidrio, el progreso, la región del paso con su nombre
          accesible y el foco al cambiar de paso viven en `FormWizard`
          (`shared/ui/wizard`). Estaban escritos a mano en esta pantalla, y
          la pestaña de detalles del producto se recorre igual: dos copias
          del mismo armazón son dos armazones que se separan poco a poco.

          Lo que se queda aquí es lo que ES de este formulario: qué se
          pinta en cada paso y qué botones lo cierran.
        */}
        <FormWizard
          steps={PRODUCT_STEPS}
          wizard={wizard}
          progressLabel="Progreso del producto"
          focusStepError={focusStepError}
          submitCount={form.formState.submitCount}
          firstStepWithError={firstStepWithError}
          actions={
            /*
              La botonera NO se la lleva el armazón. "Siguiente" en negro
              pegado a "Crear producto", "Cancelar" separado de los dos: ese
              orden es una decisión de este formulario. Ver
              `ProductFormActions`.
            */
            <ProductFormActions
              submitLabel={submitLabel}
              onCancel={() => navigate("/admin/products")}
              onBack={wizard.isFirst ? undefined : wizard.back}
              onNext={wizard.isLast ? undefined : () => void wizard.next()}
            />
          }
        >
          <ProductWizardBody step={wizard.step} locked={isEdit} />
        </FormWizard>
      </Form>
    </>
  );
}
